import type { IntlShape } from 'react-intl'
import { createSelector } from 'reselect'
import flightBundleGlossaryMessages from 'i18n/flightBundleGlossaryMessages'
import { nonNullable, sortBy } from 'lib/array/arrayUtils'
import { isOrderUpcomingOrCurrent } from 'lib/order/isUpcoming'
import { isOrderCancelled } from 'lib/order/orderUtils'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import { isOrderEligibleForChatTrip } from 'tripPlanner/hooks/orderChatTripEligibility'
import { calculateNights } from 'tripPlanner/utils'
import { getOrderTile, getOrderTileStartTime, type OrderTileData } from './bookingSelectionUtils'
import { formatFlightRouteLabel, formatFlightTypeLabel, type FlightDisplayData } from 'tripPlanner/components/Chat/flightDisplayUtils'
import { formatOrderTileDates, formatOrderTileOccupancyLabel, type TripDateFormatters } from 'tripPlanner/components/Chat/orderTileDisplayUtils'

/**
 * The order's bookings that haven't ended by `today` (local ISO date), when the order is one My Escapes
 * would list as upcoming by its dates. That check reads the live clock rather than `today`.
 */
export function getUpcomingTrip(order: App.Order, today: string): OrderTileData | undefined {
  if (isOrderCancelled(order) || !isOrderUpcomingOrCurrent(order) || !isOrderEligibleForChatTrip(order)) {
    return undefined
  }
  return getOrderTile(order, today)
}

/**
 * Soonest first by the booking each trip shows.
 * Built from the primary item's own dates so a stay's range doesn't change
 * when flight details happen to be loaded.
 */
export function buildUpcomingTrips(orders: ReadonlyArray<App.Order>, today: string): Array<OrderTileData> {
  return sortBy(nonNullable(orders.map(order => getUpcomingTrip(order, today))), getOrderTileStartTime, 'asc')
}

export const getChatLandingUpcomingTrips = createSelector(
  (state: App.State) => state.orders.orders,
  (_state: App.State, today: string) => today,
  (orders, today) => buildUpcomingTrips(Object.values(orders), today),
)

/** `flight` is the primary flight's fetched details, undefined until they load. */
export function getUpcomingTripTitle(intl: IntlShape, trip: OrderTileData, flight?: FlightDisplayData): string | undefined {
  if (trip.primaryType !== 'flight') {
    return trip.propertyName
  }
  const departureLabel = flight?.departureLabel ?? trip.flightDepartureAirport
  const arrivalLabel = flight?.arrivalLabel ?? trip.flightArrivalAirport
  if (departureLabel && arrivalLabel) {
    return formatFlightRouteLabel(intl, departureLabel, arrivalLabel)
  }
  return undefined
}

export function formatUpcomingTripDateRange(
  trip: OrderTileData,
  formatters: TripDateFormatters,
  flight?: FlightDisplayData,
): string | undefined {
  if (trip.primaryType !== 'flight') {
    return formatOrderTileDates(formatters, trip.checkInDate, trip.checkOutDate)
  }
  if (flight?.isReturnFlight && flight.departureDate && flight.arrivalDate) {
    return formatOrderTileDates(formatters, flight.departureDate, flight.arrivalDate)
  }
  return formatOrderTileDates(formatters, trip.flightDepartureDate)
}

function formatStayNights(intl: IntlShape, trip: OrderTileData): string | undefined {
  const nights = calculateNights(trip.checkInDate, trip.checkOutDate)
  if (!nights || nights <= 0) {
    return undefined
  }
  return intl.formatMessage(
    {
      id: '_EA4gF7LIC190bR2nWIw',
      defaultMessage: '{nights, plural, one {# night} other {# nights}}',
      description: 'Number of nights for an upcoming hotel booking in the trip planner chat landing page list',
    },
    { nights },
  )
}

function getTypeSegments(intl: IntlShape, trip: OrderTileData, flight?: FlightDisplayData): Array<string | undefined> {
  switch (trip.primaryType) {
    case 'hotel':
      return [
        formatStayNights(intl, trip),
        formatOrderTileOccupancyLabel(intl, trip.occupancy),
        trip.isHotelPlusFlights ? intl.formatMessage(flightBundleGlossaryMessages.hotelPlusFlights) : undefined,
      ]
    case 'flight':
      return [flight && formatFlightTypeLabel(intl, flight.isReturnFlight, flight.isDirectFlight)]
    case 'experience':
      return []
  }
}

function formatMoreBookings(intl: IntlShape, trip: OrderTileData): string | undefined {
  const flightsInBundleLabel = trip.primaryType === 'hotel' && trip.isHotelPlusFlights ? trip.flightBookingCount : 0
  const count = trip.bookingItemIds.length - 1 - flightsInBundleLabel
  if (count <= 0) {
    return undefined
  }
  return intl.formatMessage(
    {
      id: 'smpmWCO3sjOcd4kgf0FI',
      defaultMessage: '{count, plural, one {+ # more booking} other {+ # more bookings}}',
      description: 'Count of the other bookings in an order, shown in the summary of an upcoming trip on the trip planner chat landing page after the dates and guests of the booking it is named by',
    },
    { count },
  )
}

export function formatUpcomingTripSummary(
  intl: IntlShape,
  trip: OrderTileData,
  formatters: TripDateFormatters,
  flight?: FlightDisplayData,
): string {
  return nonNullable([
    formatUpcomingTripDateRange(trip, formatters, flight),
    ...getTypeSegments(intl, trip, flight),
    formatMoreBookings(intl, trip),
  ])
    .join(formatMiddleDotSeparator(intl))
}
